import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SectionHead } from "../../shared/ui";
import {
  MOBILE_QUERY,
  clamp,
  isMobileMQ,
  prefersReducedMotion,
  subscribeScroll,
} from "../../shared/motion";
import wmUrl from "../../assets/brand/n-green.svg";
import { HowPhone } from "./HowPhone";
import "./how.css";

const STEPS = [1, 2, 3, 4] as const;
/** Sticky header height: the track pins right under it. */
const HDR = 72;
/** Mobile carousel: auto-advance period, pause after a touch, snap debounce. */
const AUTO = 4600;
const HOLD = 9000;
const SETTLE = 90;
const FILL = "transform 400ms cubic-bezier(.2,.8,.2,1)";
const HOLD_EVENTS = ["pointerdown", "touchstart", "wheel"] as const;

/** Scroll distance over which the four steps play (track height minus one sticky viewport). */
const howTotal = (track: HTMLElement) => track.offsetHeight - (window.innerHeight - HDR);

const cardLeft = (list: HTMLElement, k: number) => {
  const card = list.children[k] as HTMLElement | undefined;
  return (card?.offsetLeft ?? 0) - (parseFloat(getComputedStyle(list).paddingLeft) || 0);
};

/** Where the list rests when card k is shown: its left edge, capped at the scroll end. */
const snapLeft = (list: HTMLElement, k: number) => Math.min(cardLeft(list, k), list.scrollWidth - list.clientWidth);

/** Pauses the carousel auto-advance for HOLD ms. */
const holdAuto = (until: { current: number }) => {
  until.current = Date.now() + HOLD;
};

const goCard = (list: HTMLElement, k: number, smooth = true) =>
  list.scrollTo({ left: cardLeft(list, k), behavior: smooth && !prefersReducedMotion() ? "smooth" : "auto" });

// «Qanday ishlaydi» (prototype 236–264, landing-v4.js 96–157). Desktop: a 340vh track with a sticky
// block — scroll progress fills the 4 segments and picks the step; the phone switches to that
// step's live screen (HowPhone — the handoff how-screens.html ported to React, no iframe).
// ≤1080: one column, the steps become a snap carousel (nearest card wins, 4.6 s auto-advance, 9 s
// pause after a touch). The prerender is step 1; without JS a <noscript> rule in index.html
// collapses the track.
const HowItWorks = () => {
  const { t } = useTranslation();
  const [cur, setCur] = useState(0);
  const curRef = useRef(0);
  const holdRef = useRef(0);
  /** Carousel jump, set up by the effect. */
  const goRef = useRef<(k: number) => void>(() => {});

  const trackRef = useRef<HTMLDivElement>(null);
  const hstpRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const track = trackRef.current;
    const hstp = hstpRef.current;
    const list = listRef.current;
    const stage = stageRef.current;
    const phone = phoneRef.current;
    if (!track || !hstp || !list || !stage || !phone) return;

    const mq = window.matchMedia(MOBILE_QUERY);
    const rm = prefersReducedMotion();
    const segs = Array.from(hstp.querySelectorAll<HTMLElement>("b"));
    const setStep = (i: number) => {
      if (i === curRef.current) return;
      curRef.current = i;
      setCur(i);
    };

    // Desktop: scroll progress through the track drives the segments and the step.
    const scrub = () => {
      if (mq.matches) return;
      const r = track.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      const p = clamp((HDR - r.top) / howTotal(track), 0, 1);
      segs.forEach((b, k) => {
        b.style.transform = `scaleX(${clamp(p * 4 - k, 0, 1)})`;
      });
      setStep(Math.min(STEPS.length - 1, Math.floor(p * 4)));
    };

    // Mobile: segments up to the current step are full, animated.
    const mobileFill = () =>
      segs.forEach((b, k) => {
        b.style.transition = rm ? "none" : FILL;
        b.style.transform = `scaleX(${k <= curRef.current ? 1 : 0})`;
      });

    const fitPhone = () => {
      const h = stage.clientHeight - (mq.matches ? 32 : 48);
      phone.style.setProperty("--s", clamp(h / 864, 0.44, mq.matches ? 0.6 : 0.72).toFixed(3));
    };

    // Tap / auto-advance: the step changes right away, then the list scrolls to the card.
    const go = (k: number, smooth = true) => {
      setStep(k);
      mobileFill();
      goCard(list, k, smooth);
    };
    goRef.current = go;

    // Carousel: once a swipe settles, the card nearest the left edge is the step.
    let settle = 0;
    const onListScroll = () => {
      if (!mq.matches) return;
      clearTimeout(settle);
      settle = window.setTimeout(() => {
        const x = list.scrollLeft;
        // The current card is where the list rests — keep it. On wide tablets the last cards share
        // the scroll end, and the end rule below would turn a tap on card 3 into step 4.
        if (Math.abs(snapLeft(list, curRef.current) - x) <= 4) return;
        const n = list.children.length;
        let best = 0;
        let bd = Infinity;
        for (let k = 0; k < n; k++) {
          const d = Math.abs(snapLeft(list, k) - x);
          if (d < bd) {
            bd = d;
            best = k;
          }
        }
        // The last card can't reach the left edge — at the scroll end it wins.
        if (x > 0 && x >= list.scrollWidth - list.clientWidth - 4) best = n - 1;
        setStep(best);
        mobileFill();
      }, SETTLE);
    };
    const hold = () => holdAuto(holdRef);

    let auto = 0;
    let visible = false;
    const restartAuto = () => {
      clearInterval(auto);
      auto = 0;
      if (!mq.matches || !visible || rm || document.hidden) return;
      auto = window.setInterval(() => {
        if (Date.now() < holdRef.current) return;
        go((curRef.current + 1) % STEPS.length);
      }, AUTO);
    };

    const onMQ = () => {
      segs.forEach((b) => {
        b.style.transition = mq.matches ? "" : "none";
      });
      if (mq.matches) go(curRef.current, false);
      fitPhone();
      scrub();
      restartAuto();
    };

    const unsubscribe = subscribeScroll(scrub);
    const ro = new ResizeObserver(fitPhone);
    ro.observe(stage);
    const io = new IntersectionObserver(
      (es) => {
        visible = es[0].isIntersecting;
        restartAuto();
      },
      { threshold: 0.3 },
    );
    io.observe(track);
    list.addEventListener("scroll", onListScroll, { passive: true });
    HOLD_EVENTS.forEach((ev) => list.addEventListener(ev, hold, { passive: true }));
    mq.addEventListener("change", onMQ);
    document.addEventListener("visibilitychange", restartAuto);
    onMQ();

    return () => {
      unsubscribe();
      ro.disconnect();
      io.disconnect();
      list.removeEventListener("scroll", onListScroll);
      HOLD_EVENTS.forEach((ev) => list.removeEventListener(ev, hold));
      mq.removeEventListener("change", onMQ);
      document.removeEventListener("visibilitychange", restartAuto);
      clearTimeout(settle);
      clearInterval(auto);
    };
  }, []);

  // Desktop: scroll the page to the middle of that step's slice of the track.
  // Mobile: bring the card in and pause the auto-advance.
  const onStep = (k: number) => {
    const track = trackRef.current;
    if (!track) return;
    if (isMobileMQ()) {
      holdAuto(holdRef);
      goRef.current(k);
      return;
    }
    const top = track.getBoundingClientRect().top + window.scrollY - HDR;
    window.scrollTo({
      top: top + ((k + 0.5) / STEPS.length) * howTotal(track),
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  };

  return (
    <section className="sec how" id="how">
      <div className="how-track" ref={trackRef}>
        <div className="wrap how-sticky">
          <div className="how-l">
            <SectionHead eyebrow={t("how.eyebrow")} title={t("how.title")} sub={t("how.subtitle")} />
            <div className="hstp" ref={hstpRef}>
              {STEPS.map((n) => (
                <i key={n}>
                  <b />
                </i>
              ))}
            </div>
            <div className="hs-list" ref={listRef}>
              {STEPS.map((n, k) => (
                <button
                  key={n}
                  type="button"
                  className={"hs" + (k === cur ? " on" : k < cur ? " done" : "")}
                  aria-current={k === cur ? "step" : undefined}
                  onClick={() => onStep(k)}
                >
                  <span className="hs-n">{n}</span>
                  <span className="hs-tx">
                    <span className="hs-h">{t(`how.step${n}_title`)}</span>
                    <span className="hs-p">{t(`how.step${n}_text`)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="how-r">
            <div className="how-stage" ref={stageRef}>
              <img className="wm" src={wmUrl} alt="" />
              <div className="phone-wrap" ref={phoneRef}>
                <div className="phone">
                  <div className="scr">
                    <HowPhone step={cur} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
